import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';

const apiUrls = {
  forgot: `${API_BASE_URL}/user/resetPwd`,   // sends a reset code by email
  reset: `${API_BASE_URL}/user/changePwd`,   // changes the password
};

const PasswordReset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const mode = params.get('mode') === 'reset' ? 'reset' : 'forgot';
  // invite links arrive as ?mode=reset&user=<email>&code=<one-time code>
  const invitedUser = params.get('user') || '';
  const inviteCode = params.get('code') || '';
  const [form, setForm] = useState({
    userName: invitedUser, oldPassword: '', newPassword: '', resetCode: inviteCode,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset transient state when switching between forgot and reset modes.
  useEffect(() => {
    setError('');
    setSuccess('');
    setForm(prev => ({
      ...prev,
      userName: invitedUser || prev.userName,
      resetCode: inviteCode || prev.resetCode,
    }));
  }, [mode, invitedUser, inviteCode]);

  // After a completed password change, return to sign in automatically.
  useEffect(() => {
    if (!success || mode !== 'reset') return;
    const t = setTimeout(() => navigate('/on/login'), 2500);
    return () => clearTimeout(t);
  }, [success, mode, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      let body = { userName: form.userName };
      if (mode === 'reset') {
        if (form.oldPassword) body.oldPassword = form.oldPassword;
        if (form.newPassword) body.newPassword = form.newPassword;
        if (form.resetCode) body.resetCode = form.resetCode;
      }
      const response = await fetch(apiUrls[mode], {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let errorMsg = 'Failed to update password. Please check and try again.';
        try {
          const ct = response.headers.get('content-type');
          if (ct && ct.includes('application/json')) {
            const data = await response.json();
            if (data?.error || data?.message) errorMsg = data.error || data.message;
          } else {
            const text = await response.text();
            if (text) errorMsg = text;
          }
        } catch {}
        setError(errorMsg);
        return;
      }
      setSuccess(mode === 'reset'
        ? 'Password updated. Taking you to sign in.'
        : 'Reset code sent. Check your email inbox.');
    } catch {
      setError('Failed to update password. Please check and try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: '8px',
    border: '1px solid var(--hairline)', fontSize: '14px', color: 'var(--ink)',
    background: 'var(--canvas)', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.16s, box-shadow 0.16s', fontFamily: 'inherit',
  };

  const labelStyle = {
    display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--muted)',
    textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px',
  };

  const onFocus = e => { e.target.style.borderColor = 'var(--coral)'; e.target.style.boxShadow = '0 0 0 3px var(--coral-ring)'; };
  const onBlur = e => { e.target.style.borderColor = 'var(--hairline)'; e.target.style.boxShadow = 'none'; };

  const showForm = !(mode === 'reset' && success);

  return (
    <div className="login-container">
      <div className="login-box">
        {/* One Nexus brand mark */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', margin: '0 auto 32px' }}>
          <svg viewBox="0 0 100 100" width="48" height="48" aria-hidden="true">
            <g>
              <path d="M 83.8 40.9 A 35 35 0 1 1 59.1 16.2" fill="none" stroke="var(--brand-navy)" strokeWidth="10" strokeLinecap="butt"/>
              <circle cx="74.7" cy="25.3" r="8" fill="var(--brand-blue)"/>
              <path d="M 37 65 L 37 45 A 13 13 0 0 1 63 45 L 63 65" fill="none" stroke="var(--brand-navy)" strokeWidth="10" strokeLinecap="butt"/>
            </g>
          </svg>
          <span style={{
            fontFamily: 'var(--font-wordmark)',
            fontSize: '28px',
            fontWeight: 800,
            letterSpacing: '-1px',
            lineHeight: 1,
          }}>
            <span style={{ color: 'var(--brand-blue)' }}>One</span>
            <span style={{ color: 'var(--brand-navy)' }}> Nexus</span>
          </span>
        </div>

        <h1>{mode === 'reset' ? (inviteCode ? 'Set your password' : 'Change password') : 'Forgot password'}</h1>
        <p style={{
          margin: '12px 0 32px',
          fontFamily: 'var(--font-sans)',
          fontSize: '15px', color: 'var(--body-text)', lineHeight: 1.55,
        }}>
          {mode === 'reset'
            ? (inviteCode
                ? 'Choose a password to finish setting up your account.'
                : 'Choose a new password below.')
            : 'Enter your email to receive a reset code.'}
        </p>

        {error && <div className="error-message" role="alert">{error}</div>}
        {success && (
          <div role="status" style={{ padding: '10px 14px', background: 'var(--success-bg)', border: '1px solid var(--success-border)', color: 'var(--success)', borderRadius: '8px', fontSize: '13px', fontWeight: '500', marginBottom: '14px' }}>
            {success}
          </div>
        )}

        {/* After a reset code is sent, point the user at the next step. */}
        {success && mode === 'forgot' && (
          <button
            type="button"
            className="btn-secondary"
            style={{ width: '100%', marginBottom: '16px' }}
            onClick={() => navigate(`/on/password-reset?mode=reset&user=${encodeURIComponent(form.userName)}`)}
          >
            I have my code, set a new password
          </button>
        )}

        {showForm && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label style={labelStyle} htmlFor="pr-email">Email</label>
              <input id="pr-email" type="text" name="userName" value={form.userName} onChange={handleChange} placeholder="Enter your email" required disabled={loading} style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
            </div>

            {mode === 'reset' && (
              <>
                <div className="form-group">
                  <label style={labelStyle} htmlFor="pr-new-password">New Password</label>
                  <input id="pr-new-password" type="password" name="newPassword" value={form.newPassword} onChange={handleChange} placeholder="Enter new password" autoComplete="new-password" required disabled={loading} style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
                </div>

                {!inviteCode && (
                  <>
                    <p style={{
                      margin: '4px 0 12px',
                      fontFamily: 'var(--font-sans)',
                      fontSize: '13px', color: 'var(--body-text)', lineHeight: 1.5,
                    }}>
                      Confirm it is you with one of the following: your current password,
                      or a reset code if you received one by email.
                    </p>
                    <div className="form-group">
                      <label style={labelStyle} htmlFor="pr-old-password">Current Password</label>
                      <input id="pr-old-password" type="password" name="oldPassword" value={form.oldPassword} onChange={handleChange} placeholder="Leave empty if using a reset code" autoComplete="current-password" disabled={loading} style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
                    </div>
                    <div className="form-group">
                      <label style={labelStyle} htmlFor="pr-reset-code">Reset Code</label>
                      <input id="pr-reset-code" type="text" name="resetCode" value={form.resetCode} onChange={handleChange} placeholder="Leave empty if using your current password" disabled={loading} style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
                    </div>
                  </>
                )}
              </>
            )}

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Submitting…' : mode === 'reset' ? 'Update Password' : 'Send Reset Code'}
            </button>
          </form>
        )}

        {mode === 'reset' && success && (
          <Link to="/on/login" className="btn-primary" style={{ width: '100%', textDecoration: 'none', boxSizing: 'border-box' }}>
            Go to sign in
          </Link>
        )}

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <Link
            to="/on/login"
            style={{ color: 'var(--coral)', cursor: 'pointer', fontSize: '13px', fontWeight: '500', textDecoration: 'none' }}
            onMouseOver={e => e.target.style.textDecoration = 'underline'}
            onMouseOut={e => e.target.style.textDecoration = 'none'}
          >
            ← Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
